import {Box, Button, Typography} from '@mui/material';
import {Dispatch, FC, SetStateAction, useState} from 'react';
import {useIntl} from 'react-intl';
import AMPModal from 'src/components/common/AMPModal';
import {AppDispatch, RevenueInvoiceDTO} from 'src/types';
import {Document, Page, pdfjs} from 'react-pdf/dist/umd/entry.webpack';
import {invoicesApi} from 'src/api';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {useAppDispatch} from 'src/app/hooks';
import {documentWrapperSx} from './InvoicePreview.sx';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type InvoicePdfTriggerQuery = ReturnType<typeof invoicesApi.useLazyRevenueInvoicePdfQuery>[0];

const handleGetRevenueInvoicePdf =
    (
        trigger: InvoicePdfTriggerQuery,
        dispatch: AppDispatch,
        businessId: number,
        invoiceId: number,
        setOpen: Dispatch<SetStateAction<boolean>>
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({businessId, invoiceId});
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
                setOpen(true);
            }
        };

export const RevenueInvoicePreview: FC<{ data: RevenueInvoiceDTO }> = ({data}) => {
    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const [, setNumPages] = useState<number | null>(null);
    const [pageNumber] = useState<number>(1);

    const [trigger, response] = invoicesApi.useLazyRevenueInvoicePdfQuery();
    const dispatch = useAppDispatch();

    const onDocumentLoadSuccess = ({numPages}: { numPages: number }) => {
        setNumPages(numPages);
    };
    const dialogTitle = intl.formatMessage({id: 'invoicePreview'});

    const handleOpenModal = () => {
        if (data?.business?.id) {
            handleGetRevenueInvoicePdf(trigger, dispatch, data.business.id, data.id, setOpen)();
        }
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    return (
        <Box>
            <Button
                onClick={handleOpenModal}
                sx={{p: 0}}
            >
                <Typography color="primary">{data?.business?.name}</Typography>
            </Button>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
            >
                <Box sx={documentWrapperSx}>
                    <Document
                        file={response.data}
                        renderMode="svg"
                        onLoadSuccess={onDocumentLoadSuccess}
                    >
                        <Page
                            pageNumber={pageNumber}
                            className="pdfPage"
                            renderAnnotationLayer={false}
                        />
                    </Document>
                </Box>
            </AMPModal>
        </Box>
    );
};
